import React, {useEffect, useRef} from 'react';
import styles from './styles.module.scss';
import {Scrollbar} from '@priority-software/priority-style-react';
import PartItem from './PartItem';
import {isRTL} from '../../../Localization';
import {PartsDetails} from '../types';
import {useKeyboardNavigation} from '../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {
    FETCH_BULK_TOP,
    INVENTORY_BUTTON_NAME,
    PARTS_SEARCH_MONITOR,
    PARTS_SEARCH_MONITOR_CHANGEQUANTITY,
    SEARCH_INPUT_NAME
} from '../consts';
import {useEventListener} from 'usehooks-ts';
import {Loader} from './Loader/Loader';
import {sendMonitor} from '../../../utils/monitoring';

interface Props {
    items: PartsDetails[];
    scrollbarRef: any;
    isLoading: boolean;
    focusedItemIndex: number;
    hasTquant: boolean;
    toggleSelectedItems: (partItem: PartsDetails) => void;
    selectedItems: Map<string, PartsDetails>;
    showQuantitySelector: boolean;
}

const List = ({
    items,
    scrollbarRef,
    isLoading,
    focusedItemIndex,
    hasTquant,
    toggleSelectedItems,
    selectedItems,
    showQuantitySelector
}: Props) => {
    const toggleFocusedItem = () => {
        const item = items[focusedIndex];
        const isItemSelected = !selectedItems.has(item.partName);
        toggleSelectedItems(item);
        if (
            isItemSelected &&
            showQuantitySelector &&
            (!item.selectedQuant || item.selectedQuant === 0)
        ) {
            item.selectedQuant = 1;
            sendMonitor(`${PARTS_SEARCH_MONITOR}${PARTS_SEARCH_MONITOR_CHANGEQUANTITY}`);
        }
    };

    const listContainerRef = useRef<HTMLDivElement>();
    const itemsRef = useRef([]);
    const invItemsRef = useRef<HTMLInputElement[]>([]);

    const handleFocusInventory = () => {
        const item = invItemsRef?.[focusedIndex];
        if (item) {
            item.focus();
        }
    };

    const handleRemoveInvButtonFocus = () => {
        if (document.activeElement.getAttribute('name') === INVENTORY_BUTTON_NAME) {
            const item = invItemsRef?.[focusedIndex];
            if (item) {
                listContainerRef?.current.focus();
            }
        }
    };

    const onSpace = () => {
        if (document.activeElement.getAttribute('name') === INVENTORY_BUTTON_NAME) {
            invItemsRef[focusedIndex].click();
        } else if (focusedIndex >= 0) {
            toggleFocusedItem();
        }
    };

    const onScrollToStart = () => {
        const input = document.getElementsByName(SEARCH_INPUT_NAME);
        input[0].focus();
    };

    useEffect(() => {
        setFocusedIndex(focusedItemIndex);
    }, [focusedItemIndex]);

    useEffect(() => {
        setFocusedIndex(-1);
    }, [items]);

    const {focusedIndex, handleKeyboardNavigation, setFocusedIndex} = useKeyboardNavigation({
        initialFocusedIndex: focusedItemIndex,
        pageSize: FETCH_BULK_TOP,
        listLength: items.length - 1,
        onMoveToStart: onScrollToStart,
        circleNavigation: false,
        onSpace,
        onNavigateRight: handleFocusInventory,
        onNavigateLeft: handleRemoveInvButtonFocus
    });

    const onFocusSearch = () => {
        setFocusedIndex(-1);
    };

    useEffect(() => {
        const searchInput = document.getElementsByName(SEARCH_INPUT_NAME)[0];
        if (searchInput) {
            searchInput.addEventListener('focus', onFocusSearch);
        }

        return () => {
            searchInput.removeEventListener('focus', onFocusSearch);
        };
    }, []);

    useEffect(() => {
        const item = itemsRef?.[focusedIndex];

        if (item) {
            const containerRect = listContainerRef.current.getBoundingClientRect();
            const elementRect = item.getBoundingClientRect();

            if (elementRect.top < containerRect.top || elementRect.bottom > containerRect.bottom) {
                item.scrollIntoView({behavior: 'smooth', block: 'end'});
            }
            if (document.activeElement.getAttribute('name') === INVENTORY_BUTTON_NAME) {
                handleFocusInventory();
            }
            if (document.activeElement.getAttribute('name') === SEARCH_INPUT_NAME) {
                listContainerRef?.current.focus();
            }
        }
    }, [focusedIndex]);

    const handleItemClick = (e, index) => {
        if (
            focusedIndex === index &&
            document.activeElement.getAttribute('name') !== INVENTORY_BUTTON_NAME
        ) {
            toggleFocusedItem();
        } else {
            setFocusedIndex(index);
        }
    };

    useEventListener('keyup', handleKeyboardNavigation);
    return (
        <div ref={listContainerRef} tabIndex={0}>
            <Scrollbar scrollbarRef={scrollbarRef} rtl={isRTL()}>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className={styles.itemsContainer}>
                        {items?.map((part, index) => (
                            <PartItem
                                onSelectItem={toggleSelectedItems}
                                innerRef={el => (itemsRef[index] = el)}
                                invButtonRef={el => (invItemsRef[index] = el)}
                                onClick={e => handleItemClick(e, index)}
                                isFocused={index === focusedIndex}
                                key={part.partName}
                                part={part}
                                onFocusCheckBox={() => setFocusedIndex(index)}
                                hasTquant={hasTquant}
                            />
                        ))}
                    </div>
                )}
            </Scrollbar>
        </div>
    );
};
export default List;
